import {
    Button,
    Combobox,
    Label,
    makeStyles,
    mergeClasses,
    Option,
    OptionOnSelectData,
    SelectionEvents,
    Textarea,
} from '@fluentui/react-components';
import { useBoolean } from '@fluentui/react-hooks';
import cloneDeep from 'lodash/cloneDeep';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetCalendarPeriods, useGetCases } from '../../api/context/context';
import { useIsFeatureEnabled } from '../../api/features/features';
import { useDefaultModalStyles } from '../../hooks/useDefaultModalStyles';
import { useHiddenComboboxCheckIconStyles } from '../../hooks/useHiddenComboboxCheckIconStyles';
import { ICalendarPeriod, ICase, TaxReturnItemRecord } from '../../model';
import { contextChanged } from '../../utils/ReportUtils';
import { ShowableTaxReturnItemRecord } from '../add-return/AddReturnPanel';
import { DefaultTooltipHostV9 } from '../common/DefaultTooltipHostV9';
import Modal from '../common/ModalV9';
import { LISTBOX_MAX_HEIGHT } from '../common/VirtualizedOptionList';
import WarningLabel from '../common/WarningLabel';
import EditReturnUnsavedChangesModal from '../edit-return/EditReturnUnsavedChangesModal';
import { EMPTY_CONTEXT_KEY_VALUE } from '../tables/reports-table/ReportsTable';
import AdvancedEntityOptions from './AdvancedEntityOptions';

const useStyles = makeStyles({
    label: {
        fontWeight: 'normal',
        marginTop: '5px',
        padding: '5px 0',
    },
    flexContainer: {
        display: 'flex',
    },
    columnContainer: {
        flexDirection: 'column',
        flex: 1,
    },
    gap20Container: {
        gap: '20px',
    },
    gap40Container: {
        gap: '40px',
    },
    mainContentContainer: {
        flex: 0.6,
        minHeight: '552px',
        width: '400px',
    },
    secondaryContentContainer: {
        flex: 0.4,
        marginTop: '5px',
    },
    warningMessage: {
        fontSize: '14px',
    },
    descriptionContainer: {
        padding: '5px 0',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    textField: {
        width: '100%',
    },
    combobox: {
        width: '100%',
        minWidth: '100%',
    },
    listbox: {
        maxHeight: `${LISTBOX_MAX_HEIGHT}px !important`,
        overflowY: 'auto',
    },
});

export interface EditReturnModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedReturn: ShowableTaxReturnItemRecord | undefined;
    onSubmit: (value: TaxReturnItemRecord | undefined) => void;
    onDismissed?: () => void;
    allReturnsNames?: string[];
    hasEditTaxReturnsDataEnabled?: boolean;
}

const REPORT_NAME_MAX_LENGTH = 128;

const EditReturnModal: React.FC<EditReturnModalProps> = ({
    isOpen,
    onClose,
    selectedReturn,
    onSubmit,
    onDismissed,
    allReturnsNames,
    hasEditTaxReturnsDataEnabled = true,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { hidden: hiddenCheckIcon } = useHiddenComboboxCheckIconStyles();
    const [taxReturnData, setTaxReturnData] = useState<ShowableTaxReturnItemRecord | undefined>(undefined);
    const { data: cases } = useGetCases();
    const { data: taxYears } = useGetCalendarPeriods();
    const [returnName, setReturnName] = useState<string>('');
    const [hasChanges, { setTrue: setHasChangesTrue, setFalse: setHasChangesFalse }] = useBoolean(false);

    const { data: isEditCalculationEnhancementsEnabled } = useIsFeatureEnabled({
        featureFlagName: 'EditCalculationEnhancementsEnabled',
    });

    const [isEditConfirmationReturnModalOpen, { setTrue: openEditConfirmationReturnModal, setFalse: closeEditConfirmationReturnModal }] =
        useBoolean(false);

    const [isUnsavedChangesModalOpen, { setTrue: openUnsavedChangesModal, setFalse: closeUnsavedChangesModal }] = useBoolean(false);

    const isDuplicatedName = useMemo(() => {
        if (!isEditCalculationEnhancementsEnabled) {
            return false;
        }

        return allReturnsNames?.includes(returnName);
    }, [returnName, allReturnsNames]);

    useEffect(() => {
        if (selectedReturn) {
            const initialTaxReturnData = cloneDeep(selectedReturn);
            setTaxReturnData(initialTaxReturnData);

            if (returnName === '') {
                setReturnName(selectedReturn.taxReturnItemName ?? getUpdatedTaxReturnName(initialTaxReturnData));
            }
        }
    }, [selectedReturn]);

    const getUpdatedTaxReturnName = (taxReturnData: ShowableTaxReturnItemRecord | undefined) => {
        return `${taxReturnData?.entityCode} ${taxReturnData?.calculationName} ${taxReturnData?.taxReturnItemDescription?.trim()} ${t(
            'case'
        ).toString()} ${taxReturnData?.caseCode}, ${taxReturnData?.taxYearName}`;
    };

    const closeModal = (): void => {
        onClose();
        onDismissed?.();
    };

    const onChangeDescription = (value: string | undefined) => {
        const valueWithoutBreakLines = value?.replace(/\n/g, '') ?? '';
        const updatedReturnName = getUpdatedTaxReturnName({ ...taxReturnData, taxReturnItemDescription: valueWithoutBreakLines });

        if (updatedReturnName.length > REPORT_NAME_MAX_LENGTH) {
            return;
        }

        setReturnName(updatedReturnName);
        setTaxReturnData({ ...taxReturnData, taxReturnItemName: updatedReturnName, taxReturnItemDescription: valueWithoutBreakLines });
        setHasChangesTrue();
    };

    const onChangeCase = (_event: SelectionEvents, data: OptionOnSelectData) => {
        const selectedCase = cases?.find((caseItem: ICase) => String(caseItem.key) === data.optionValue);
        if (!selectedCase) {
            return;
        }

        const updatedReturnName = getUpdatedTaxReturnName({
            ...taxReturnData,
            taxReturnItemCaseKey: selectedCase.key,
            caseCode: selectedCase.code,
        });

        setReturnName(updatedReturnName);
        setTaxReturnData({
            ...taxReturnData,
            taxReturnItemName: updatedReturnName,
            taxReturnItemCaseKey: selectedCase.key,
            caseCode: selectedCase.code,
        });
        setHasChangesTrue();
    };

    const onChangePeriod = (_event: SelectionEvents, data: OptionOnSelectData) => {
        const selectedPeriod = taxYears?.find((period: ICalendarPeriod) => String(period.key) === data.optionValue);
        if (!selectedPeriod) {
            return;
        }

        const updatedReturnName = getUpdatedTaxReturnName({
            ...taxReturnData,
            taxReturnItemPeriodKey: selectedPeriod.key,
            taxYearName: selectedPeriod.name,
        });

        setReturnName(updatedReturnName);
        setTaxReturnData({
            ...taxReturnData,
            taxReturnItemName: updatedReturnName,
            taxReturnItemPeriodKey: selectedPeriod.key,
            taxYearName: selectedPeriod.name,
        });
        setHasChangesTrue();
    };

    const selectedCaseValue = hasEditTaxReturnsDataEnabled
        ? String(taxReturnData?.taxReturnItemCaseKey ?? '')
        : String(taxReturnData?.caseCode ?? '');
    const selectedCaseDisplay =
        cases?.find((caseItem: ICase) =>
            hasEditTaxReturnsDataEnabled ? String(caseItem.key) === selectedCaseValue : caseItem.code === selectedCaseValue
        )?.displayName ?? '';

    const selectedTaxYearValue = hasEditTaxReturnsDataEnabled
        ? String(taxReturnData?.taxReturnItemPeriodKey ?? '')
        : String(taxReturnData?.taxYearName ?? '');
    const selectedTaxYearDisplay = hasEditTaxReturnsDataEnabled
        ? taxYears?.find((period: ICalendarPeriod) => String(period.key) === selectedTaxYearValue)?.name ?? ''
        : taxReturnData?.taxYearName ?? '';

    const bodyContent = (
        <>
            {!!taxReturnData && (
                <div className={mergeClasses(styles.flexContainer, styles.gap40Container)}>
                    <div className={mergeClasses(styles.flexContainer, styles.columnContainer, styles.mainContentContainer)}>
                        <Label className={styles.label}>{t('reportName').toString()}</Label>
                        <Textarea
                            id='reportName'
                            className={styles.textField}
                            aria-label={t('reportName').toString()}
                            placeholder={t('reportName').toString()}
                            value={returnName || ''}
                            disabled
                            resize='none'
                        />

                        <div className={styles.descriptionContainer}>
                            <Label className={styles.label}>{t('description').toString()}</Label>
                            {hasEditTaxReturnsDataEnabled ? (
                                <Textarea
                                    id='description'
                                    className={styles.textField}
                                    aria-label={t('description').toString()}
                                    placeholder={t('description').toString()}
                                    value={taxReturnData?.taxReturnItemDescription || ''}
                                    onChange={(_, data) => onChangeDescription(data.value)}
                                    resize='none'
                                />
                            ) : (
                                <DefaultTooltipHostV9
                                    relationship='description'
                                    appearance='inverted'
                                    withArrow
                                    positioning='after'
                                    content={t('noFunctionalAccess').toString()}
                                    alwaysDisplay
                                >
                                    <Textarea
                                        id='description'
                                        className={styles.textField}
                                        aria-label={t('description').toString()}
                                        placeholder={t('description').toString()}
                                        value={taxReturnData?.taxReturnItemDescription || ''}
                                        disabled
                                        resize='none'
                                    />
                                </DefaultTooltipHostV9>
                            )}
                        </div>
                        <Label className={styles.label}>{t('formOrCalculation').toString()}</Label>
                        <Combobox
                            className={styles.combobox}
                            disabled
                            value={taxReturnData?.calculationDescription ?? ''}
                            selectedOptions={[String(taxReturnData?.calculationKey ?? '')]}
                            data-testid='formOrCalculation-input'
                            listbox={{ className: styles.listbox }}
                        >
                            <Option
                                className={hiddenCheckIcon}
                                value={String(taxReturnData?.calculationKey ?? '')}
                                text={taxReturnData?.calculationDescription ?? ''}
                            >
                                {taxReturnData?.calculationDescription}
                            </Option>
                        </Combobox>

                        <Label className={styles.label}>{t('entityOrEntityGroup').toString()}</Label>
                        <Combobox
                            className={styles.combobox}
                            disabled
                            value={taxReturnData?.entityLabel ?? ''}
                            selectedOptions={[String(taxReturnData?.entityKey ?? '')]}
                            data-testid='entityOrEntityGroup-input'
                            listbox={{ className: styles.listbox }}
                        >
                            <Option
                                className={hiddenCheckIcon}
                                value={String(taxReturnData?.entityKey ?? '')}
                                text={taxReturnData?.entityLabel ?? ''}
                            >
                                {taxReturnData?.entityLabel}
                            </Option>
                        </Combobox>

                        <Label className={styles.label}>{t('jurisdiction').toString()}</Label>
                        <Combobox
                            className={styles.combobox}
                            disabled
                            value={taxReturnData?.jurisdictionCode ?? ''}
                            selectedOptions={[taxReturnData?.jurisdictionCode ?? '']}
                            data-testid='jurisdiction-input'
                            listbox={{ className: styles.listbox }}
                        >
                            <Option
                                className={hiddenCheckIcon}
                                value={taxReturnData?.jurisdictionCode ?? ''}
                                text={taxReturnData?.jurisdictionCode ?? ''}
                            >
                                {taxReturnData?.jurisdictionCode}
                            </Option>
                        </Combobox>

                        <Label className={styles.label}>{t('case').toString()}</Label>
                        <Combobox
                            className={styles.combobox}
                            disabled={!hasEditTaxReturnsDataEnabled}
                            value={selectedCaseDisplay}
                            selectedOptions={selectedCaseValue ? [selectedCaseValue] : []}
                            onOptionSelect={onChangeCase}
                            data-testid='case-input'
                            listbox={{ className: styles.listbox }}
                            inlinePopup
                        >
                            {cases?.map((caseItem: ICase) => (
                                <Option
                                    key={String(caseItem.key)}
                                    className={hiddenCheckIcon}
                                    value={String(caseItem.key)}
                                    text={caseItem.displayName ?? ''}
                                >
                                    {caseItem.displayName}
                                </Option>
                            ))}
                        </Combobox>

                        <Label className={styles.label}>{t('taxYear').toString()}</Label>
                        <Combobox
                            className={styles.combobox}
                            disabled={!hasEditTaxReturnsDataEnabled}
                            value={selectedTaxYearDisplay}
                            selectedOptions={selectedTaxYearValue ? [selectedTaxYearValue] : []}
                            onOptionSelect={onChangePeriod}
                            data-testid='taxYear-input'
                            listbox={{ className: styles.listbox }}
                            inlinePopup
                        >
                            {hasEditTaxReturnsDataEnabled
                                ? taxYears?.map((period: ICalendarPeriod) => (
                                      <Option
                                          key={String(period.key)}
                                          className={hiddenCheckIcon}
                                          value={String(period.key)}
                                          text={period.name ?? ''}
                                      >
                                          {period.name}
                                      </Option>
                                  ))
                                : [
                                      <Option
                                          key={selectedTaxYearValue}
                                          className={hiddenCheckIcon}
                                          value={selectedTaxYearValue}
                                          text={selectedTaxYearDisplay}
                                      >
                                          {selectedTaxYearDisplay}
                                      </Option>,
                                  ]}
                        </Combobox>
                    </div>

                    <div className={styles.secondaryContentContainer}>
                        <AdvancedEntityOptions
                            setHasChanges={setHasChangesTrue}
                            taxReturnData={taxReturnData}
                            setTaxReturn={setTaxReturnData}
                            hasEditTaxReturnsDataEnabled={hasEditTaxReturnsDataEnabled}
                        />
                    </div>
                </div>
            )}
        </>
    );

    const submit = () => {
        if (taxReturnData) {
            taxReturnData.taxReturnItemName = returnName;
            taxReturnData.taxReturnItemDescription = taxReturnData.taxReturnItemDescription?.trim();
            if (contextChanged(taxReturnData, selectedReturn)) {
                taxReturnData.contextKey = EMPTY_CONTEXT_KEY_VALUE;
            }
            setReturnName('');
            onSubmit(taxReturnData);
        }
        setHasChangesFalse();
        closeModal();
    };

    const tryCloseOrShowWarning = (): boolean => {
        if (isEditCalculationEnhancementsEnabled && hasChanges && !isUnsavedChangesModalOpen) {
            openUnsavedChangesModal();
            return false;
        }

        clearStateAndClose();
        return true;
    };

    const clearStateAndClose = () => {
        setReturnName('');
        setTaxReturnData(undefined);
        setHasChangesFalse();
        closeUnsavedChangesModal();

        closeModal();
    };

    const handleSaveConfirmation = () => {
        if (isUnsavedChangesModalOpen) {
            closeUnsavedChangesModal();
        }

        const hasContextChanged = taxReturnData?.taxReturnItemId && contextChanged(taxReturnData, selectedReturn);
        if (isEditCalculationEnhancementsEnabled && hasContextChanged) {
            openEditConfirmationReturnModal();
            return;
        }
        submit();
    };

    const confirmEditReturn = () => {
        closeEditConfirmationReturnModal();
        submit();
    };

    const { button, content } = useDefaultModalStyles();

    const editReturnModalButtons = (
        <>
            <Button
                appearance='primary'
                disabled={!hasChanges || !hasEditTaxReturnsDataEnabled}
                size={'small'}
                onClick={handleSaveConfirmation}
                data-testid='save-edit-return'
                aria-label={t('save').toString()}
                className={button}
            >
                {t('save')}
            </Button>

            <Button
                size={'small'}
                onClick={tryCloseOrShowWarning}
                data-testid='close-edit-return'
                aria-label={t('close').toString()}
                className={button}
            >
                {t('close')}
            </Button>
        </>
    );

    const confirmModalButtons = (
        <>
            <Button
                appearance='primary'
                disabled={!hasChanges}
                size={'small'}
                onClick={confirmEditReturn}
                data-testid='confirm-edit-return'
                aria-label={t('save').toString()}
                className={button}
            >
                {t('save')}
            </Button>
            <Button
                size='small'
                onClick={closeEditConfirmationReturnModal}
                data-testid='cancel-edit-return'
                aria-label={t('cancel').toString()}
                className={button}
            >
                {t('cancel')}
            </Button>
        </>
    );

    return (
        <>
            <Modal
                id='edit-return-modal'
                headerContent={t('editCalculation').toString()}
                bodyContent={bodyContent}
                leftFooterElement={
                    isDuplicatedName ? (
                        <WarningLabel
                            warningMessage={t('thisCalculationAlreadyExists')}
                            labelClassName={styles.warningMessage}
                            iconTestId='duplicated-return-icon'
                            role='status'
                        />
                    ) : undefined
                }
                buttons={editReturnModalButtons}
                isOpen={isOpen}
                onClose={tryCloseOrShowWarning}
                modalMaxWidthInPixels={852}
            />
            <Modal
                headerContent={t('confirm')}
                bodyContent={<div className={content}>{t('confirmReturnEditDescription')}</div>}
                isOpen={isEditConfirmationReturnModalOpen}
                onClose={closeEditConfirmationReturnModal}
                buttons={confirmModalButtons}
                elementToFocusOnDismiss={document.getElementById('edit-return-modal') || undefined}
            />
            <EditReturnUnsavedChangesModal
                isModalVisible={isUnsavedChangesModalOpen}
                onCancel={closeUnsavedChangesModal}
                onSave={handleSaveConfirmation}
                onLeave={clearStateAndClose}
            />
        </>
    );
};

export default EditReturnModal;
